/**
 * Textured MSI blue (gradient + noise + sheen) for buttons and bars.
 * Matches CareMap top-bar SVG; use instead of flat #1B4584 fills.
 */

/** CareMap / HealthDesk primary bar: gradient + noise + sheen + edge lines (canonical asset). */
const MSI_BLUE_TEXTURED_SVG = `<svg width="1600" height="120" viewBox="0 0 1600 120" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="none">
  <defs>
    <linearGradient id="blueGrad" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="#2a5a9f"/>
      <stop offset="18%" stop-color="#1B4584"/>
      <stop offset="40%" stop-color="#163a6f"/>
      <stop offset="60%" stop-color="#235394"/>
      <stop offset="82%" stop-color="#1B4584"/>
      <stop offset="100%" stop-color="#0f2c57"/>
    </linearGradient>
    <filter id="blueTexture" x="-10%" y="-10%" width="120%" height="120%">
      <feTurbulence type="fractalNoise" baseFrequency="0.9 0.02" numOctaves="2" seed="4" result="noise"/>
      <feColorMatrix in="noise" type="saturate" values="0" result="mono"/>
      <feComponentTransfer>
        <feFuncA type="linear" slope="0.06"/>
      </feComponentTransfer>
    </filter>
    <linearGradient id="blueSheen" x1="0%" y1="0%" x2="100%" y2="0%">
      <stop offset="0%" stop-color="rgba(255,255,255,0)"/>
      <stop offset="16%" stop-color="rgba(255,255,255,0.18)"/>
      <stop offset="38%" stop-color="rgba(255,255,255,0.04)"/>
      <stop offset="62%" stop-color="rgba(255,255,255,0.14)"/>
      <stop offset="100%" stop-color="rgba(255,255,255,0)"/>
    </linearGradient>
  </defs>
  <rect width="1600" height="120" fill="url(#blueGrad)"/>
  <rect width="1600" height="120" filter="url(#blueTexture)" opacity="0.55"/>
  <rect width="1600" height="120" fill="url(#blueSheen)" opacity="0.5"/>
  <line x1="0" y1="1" x2="1600" y2="1" stroke="rgba(255,255,255,0.22)" stroke-width="2"/>
  <line x1="0" y1="119" x2="1600" y2="119" stroke="rgba(0,0,0,0.22)" stroke-width="2"/>
</svg>`;

const MSI_BLUE_TEXTURE_DATA_URI = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(MSI_BLUE_TEXTURED_SVG)}`;

/** Full CSS `url("data:...")` value for composing layered backgrounds */
export const MSI_BLUE_TEXTURED_BG_IMAGE = `url("${MSI_BLUE_TEXTURE_DATA_URI}")`;

const HOVER_OVERLAY = 'linear-gradient(rgba(0,0,0,0.12), rgba(0,0,0,0.12))';

/** MUI `sx` fragment: textured fill only (no text color). */
export const MSI_BLUE_TEXTURED_FILL_SX = {
  backgroundColor: '#1B4584',
  backgroundImage: MSI_BLUE_TEXTURED_BG_IMAGE,
  backgroundSize: '100% 100%',
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'center',
};

/** Background longhands for hover on textured primary buttons */
export const MSI_BLUE_TEXTURED_HOVER_BG_SX = {
  backgroundColor: '#1B4584',
  backgroundImage: `${MSI_BLUE_TEXTURED_BG_IMAGE}, ${HOVER_OVERLAY}`,
  backgroundSize: '100% 100%, 100% 100%',
  backgroundRepeat: 'no-repeat, no-repeat',
  backgroundPosition: 'center, center',
};

/** Contained primary: white label, textured fill, darkening hover, flat disabled */
export const MSI_BLUE_TEXTURED_CONTAINED_BUTTON_SX = {
  ...MSI_BLUE_TEXTURED_FILL_SX,
  color: '#fff',
  boxShadow: 'none',
  '&:hover': {
    boxShadow: 'none',
    ...MSI_BLUE_TEXTURED_HOVER_BG_SX,
  },
  '&:disabled': {
    backgroundColor: 'rgba(27, 69, 132, 0.4)',
    backgroundImage: 'none',
    color: 'rgba(255,255,255,0.75)',
  },
};

/** Filled Chip (e.g. status) */
export const MSI_BLUE_TEXTURED_CHIP_SX = {
  ...MSI_BLUE_TEXTURED_FILL_SX,
  color: '#fff',
  border: 'none',
  fontWeight: 500,
  '&:hover': {
    ...MSI_BLUE_TEXTURED_HOVER_BG_SX,
  },
};

/**
 * MUI `components.MuiSwitch.styleOverrides.root` — primary `color="primary"` checked track.
 */
export const MSI_BLUE_TEXTURED_MUI_SWITCH_ROOT_SX = {
  '& .MuiSwitch-switchBase.Mui-checked.MuiSwitch-colorPrimary + .MuiSwitch-track': {
    ...MSI_BLUE_TEXTURED_FILL_SX,
    opacity: 1,
    border: 'none',
  },
  '& .MuiSwitch-switchBase.Mui-checked.MuiSwitch-colorPrimary': {
    color: '#fff',
  },
  '& .MuiSwitch-switchBase.Mui-checked.MuiSwitch-colorPrimary:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.08)',
  },
  '& .MuiSwitch-switchBase.Mui-disabled.Mui-checked + .MuiSwitch-track': {
    backgroundImage: 'none',
    backgroundColor: 'rgba(27, 69, 132, 0.38)',
  },
};

/** MUI Slider root when `color="primary"`: textured track, white thumb with blue rim. */
export const MSI_BLUE_TEXTURED_MUI_SLIDER_PRIMARY_ROOT_SX = {
  '&.MuiSlider-colorPrimary .MuiSlider-track': {
    ...MSI_BLUE_TEXTURED_FILL_SX,
    border: 'none',
  },
  '&.MuiSlider-colorPrimary .MuiSlider-thumb': {
    backgroundColor: '#fff',
    border: '2px solid #1B4584',
  },
};

/** MUI LinearProgress colored bar (`color="primary"`). */
export const MSI_BLUE_TEXTURED_MUI_LINEAR_PROGRESS_BAR_PRIMARY_SX = {
  ...MSI_BLUE_TEXTURED_FILL_SX,
};

/**
 * MUI ToggleButton: selected + `color="primary"` (use with styleOverrides.root callback `ownerState`).
 * @param {{ hover?: boolean }} [opts]
 */
export function msiBlueTexturedMuiToggleButtonPrimarySelectedSx(opts = {}) {
  const { hover = true } = opts;
  const base = {
    ...MSI_BLUE_TEXTURED_FILL_SX,
    color: '#fff',
    borderColor: 'transparent',
  };
  if (!hover) {
    return base;
  }
  return {
    ...base,
    '&:hover': {
      ...MSI_BLUE_TEXTURED_HOVER_BG_SX,
      color: '#fff',
      borderColor: 'transparent',
    },
  };
}
